import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, MapPin, Loader2, Handshake, TrendingUp, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

export default function PartnerRecruit() {
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", district: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Staňte sa partnerom v okrese | Zateplenia.eu";
    api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.name || !form.email) return toast.error("Vyplňte meno a e-mail.");
    setBusy(true);
    try {
      await api.post("/partner-applications", form);
      setDone(true);
      toast.success("Žiadosť odoslaná. Ozveme sa vám.");
    } catch { toast.error("Odoslanie zlyhalo."); } finally { setBusy(false); }
  };

  const free = districts.filter((d) => !d.has_partner);

  return (
    <div>
      <Header />
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src="https://images.pexels.com/photos/27134625/pexels-photo-27134625.jpeg?auto=compress&cs=tinysrgb&w=1600" alt="" className="w-full h-full object-cover grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/50" />
        </div>
        <div className="relative z-10 max-w-[1100px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6"><Handshake className="w-4 h-4" /> Partnerský program</div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.9] text-5xl sm:text-7xl lg:text-8xl max-w-4xl">
            Staňte sa partnerom<br /><span className="text-gold">vo vašom okrese</span>
          </motion.h1>
          <p className="text-white/70 text-lg max-w-2xl mt-6">Prijímame <strong className="text-white">jedného exkluzívneho realizátora na okres</strong>. Prihláste sa skôr, než vás predbehne konkurencia.</p>
        </div>
      </section>

      <section className="bg-ink py-16 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[[ShieldCheck, "Exkluzivita na okres", "Iba jeden partner na okres. Žiadne delenie dopytov s konkurenciou."],
            [TrendingUp, "Stály prísun dopytov", "Kalkulačka a SEO vám privádzajú klientov pripravených zatepľovať."],
            [Handshake, "Vlastné ceny + provízia", "Nastavíte si vlastnú cenu prác, my si účtujeme len dohodnutú províziu."]].map(([Icon, t, d]) => (
            <div key={t} className="p-8 border border-white/10 bg-ink-2">
              <Icon className="w-8 h-8 text-gold mb-4" />
              <h3 className="font-display text-2xl text-white mb-2">{t}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ink-2 py-20 border-t border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-4">Voľné okresy ({free.length})</p>
            <div className="flex flex-wrap gap-2 max-h-80 overflow-y-auto pr-2">
              {districts.map((d) => (
                <span key={d.name} className={`text-xs px-3 py-1.5 border flex items-center gap-1 ${d.has_partner ? "border-white/10 text-white/30 line-through" : "border-gold/40 text-gold"}`}>
                  <MapPin className="w-3 h-3" /> {d.name}
                </span>
              ))}
            </div>
          </div>

          {done ? (
            <div className="border border-gold/40 bg-gold/5 p-8 flex flex-col items-center justify-center text-center">
              <Check className="w-12 h-12 text-gold mb-4" />
              <h3 className="font-display text-3xl text-white">Ďakujeme!</h3>
              <p className="text-white/60 mt-2">Vašu žiadosť sme prijali a čoskoro vás kontaktujeme.</p>
            </div>
          ) : (
            <div className="border border-white/10 bg-ink p-6 md:p-8 space-y-3" data-testid="recruit-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input placeholder="Meno *" data-testid="recruit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
                <input placeholder="Firma" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
                <input placeholder="E-mail *" data-testid="recruit-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
                <input placeholder="Telefón" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
              </div>
              <select value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="recruit-district"
                className="w-full bg-ink border border-white/20 focus:border-gold outline-none text-white px-4 py-3">
                <option value="">Vyberte okres…</option>
                {districts.map((d) => <option key={d.name} value={d.name} disabled={d.has_partner}>{d.name}{d.has_partner ? " (obsadené)" : ""}</option>)}
              </select>
              <textarea placeholder="Krátko o vás — skúsenosti, referencie…" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3 resize-none" />
              <button onClick={submit} disabled={busy} data-testid="recruit-submit" className="w-full bg-gold text-ink py-4 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Odoslať žiadosť o partnerstvo"}
              </button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
